import React, { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { PatrolViewerEmptyState } from "./PatrolViewerEmptyState";
import { PatrolLiveMonitoringEmptyState } from "./PatrolLiveMonitoringEmptyState";
import { PatrolVideoPlayer } from "./PatrolVideoPlayer";
import { Patrol360Player } from "./Patrol360Player";
import { PatrolLivePreviewToolbar } from "./PatrolLivePreviewToolbar.jsx";
import { resolvePatrolMediaUrl } from "./patrolMediaUrls";
import { DEFAULT_PATROL_ROTATION_DURATION_SECONDS } from "./patrolRotationSpeed";
import { PATROL_PTZ_PREVIEW_SHARE_DEFAULT } from "./patrolPtzPreviewSize";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  ViewAgenda,
  ViewColumn,
  ChevronRight,
  ChevronLeft,
} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

export function PatrolViewer({
  selectedPatrol,
  selectedPatrolSequenceId,
  currentVideoIndex,
  setCurrentVideoIndex,
  autoPlay = true,
  rotationDurationSeconds = DEFAULT_PATROL_ROTATION_DURATION_SECONDS,
  onSettingsClick,
  showCompareControls = false,
  patrolListCollapsed = false,
  onTogglePatrolListCollapse,
  onTogglePanelExpand,
  isPanelExpanded = false,
  panelId,
  // Sync mode props (optional; when lock is on in Compare Patrol)
  syncMode = false,
  controlledIsPlaying,
  controlledCurrentTime,
  controlledVideoIndex,
  onPlayPauseChange,
  onTimeUpdate,
  onVideoIndexChange,
  controlledYaw,
  controlledPitch,
  onRotationChange,
  controlledFov,
  onFovChange,
  controlledViewMode,
  onViewModeChange,
  controlledAutoRotate,
  onAutoRotateChange,
  // Image viewer props
  imageUrl = null,
  pins = [],
  // Responsive layout prop
  leftPanelWidth = 35,
  module = "construction-site-management",
  selectedPatrolId,
  /** Live monitoring tab only: extra floor-plan toolbar controls in Patrol360Player. */
  showLiveMonitoringFloorControls = false,
  liveMonitoring = false,
  liveHlsUrl = null,
  liveMonitoringLoading = false,
  ptzPreviewSharePct = PATROL_PTZ_PREVIEW_SHARE_DEFAULT,
}) {
  const [livePreviewToolbar, setLivePreviewToolbar] = useState(null);
  const handleLivePreviewToolbarChange = useCallback((props) => {
    setLivePreviewToolbar(props);
  }, []);

  const hasSelectedPatrol = selectedPatrol?.length > 0 ? true : false;
  const hasFloorPlanImage = Boolean(imageUrl);
  const isLiveMonitoringEmpty =
    liveMonitoring &&
    !liveMonitoringLoading &&
    !hasSelectedPatrol &&
    !hasFloorPlanImage &&
    !liveHlsUrl;
  const showPatrol360Player = liveMonitoring
    ? !liveMonitoringLoading &&
      (hasSelectedPatrol || hasFloorPlanImage || Boolean(liveHlsUrl))
    : hasSelectedPatrol;
  const videoUrls = useMemo(() => {
    return (
      selectedPatrol?.map((patrol) =>
        resolvePatrolMediaUrl(patrol.recording_path),
      ) || []
    );
  }, [selectedPatrol]);

  const getPatrolListButtonConfig = () =>
    patrolListCollapsed
      ? {
          icon: <ViewAgenda />,
          tooltip: "Show Patrol List",
          ariaLabel: "Show Patrol List panel",
        }
      : {
          icon: <ViewColumn />,
          tooltip: "Focus on Video",
          ariaLabel: "Hide Patrol List panel",
        };

  const getPanelExpandButtonConfig = () => {
    if (panelId === "B") {
      return isPanelExpanded
        ? { icon: <ChevronRight />, tooltip: "Exit Fullscreen" }
        : { icon: <ChevronLeft />, tooltip: "Fullscreen Mode" };
    }

    return isPanelExpanded
      ? { icon: <ChevronLeft />, tooltip: "Exit Fullscreen" }
      : { icon: <ChevronRight />, tooltip: "Fullscreen Mode" };
  };

  const patrolListBtn = getPatrolListButtonConfig();
  const panelExpandBtn = getPanelExpandButtonConfig();

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "white",
        border: "1px solid",
        borderColor: "#E5E7EB",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "white",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderBottomColor: "#E5E7EB",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#FF6B35",
            fontWeight: 600,
            fontSize: "1.125rem",
          }}
        >
          Smart Stadium Patrolling
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {hasSelectedPatrol &&
            showCompareControls &&
            onTogglePatrolListCollapse && (
              <Tooltip title={patrolListBtn.tooltip}>
                <IconButton onClick={onTogglePatrolListCollapse}>
                  {patrolListBtn.icon}
                </IconButton>
              </Tooltip>
            )}

          {showCompareControls && onTogglePanelExpand && (
            <Tooltip title={panelExpandBtn.tooltip}>
              <IconButton
                onClick={onTogglePanelExpand}
                sx={{
                  bgcolor: "#2932E5",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#1f26b8 !important",
                    backgroundColor: "#1f26b8 !important",
                  },
                }}
              >
                {panelExpandBtn.icon}
              </IconButton>
            </Tooltip>
          )}

          {livePreviewToolbar ? (
            <PatrolLivePreviewToolbar {...livePreviewToolbar} />
          ) : null}

          {onSettingsClick && (
            <IconButton
              size="small"
              onClick={onSettingsClick}
              sx={{
                width: 24,
                height: 24,
                bgcolor: "#F3F4F6",
                "&:hover": {
                  bgcolor: "#E5E7EB",
                },
              }}
              title="Settings"
            >
              <SettingsOutlinedIcon sx={{ fontSize: 25, color: "#6B7280" }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: module === "compare-patrol" ? "hidden" : "auto",
          position: "relative",
        }}
      >
        {liveMonitoringLoading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              color: "#6B7280",
            }}
          >
            <CircularProgress size={40} />
            <Typography sx={{ fontSize: 14 }}>
              Loading patrol drawing and cameras…
            </Typography>
          </Box>
        ) : isLiveMonitoringEmpty ? (
          <PatrolLiveMonitoringEmptyState />
        ) : showPatrol360Player ? (
          <Box sx={{ height: "100%", width: "100%", minWidth: 0 }}>
            {liveMonitoring || videoUrls.length > 0 ? (
              module !== "compare-patrol" ? (
                <Patrol360Player
                  autoPlay={autoPlay}
                  rotationDurationSeconds={rotationDurationSeconds}
                  currentVideoIndex={currentVideoIndex}
                  setCurrentVideoIndex={setCurrentVideoIndex}
                  imageUrl={imageUrl}
                  selectedPatrol={selectedPatrol}
                  showLiveMonitoringFloorControls={
                    showLiveMonitoringFloorControls
                  }
                  liveMonitoring={liveMonitoring}
                  liveHlsUrl={liveHlsUrl}
                  liveMonitoringLoading={liveMonitoringLoading}
                  ptzPreviewSharePct={ptzPreviewSharePct}
                  onLivePreviewToolbarChange={handleLivePreviewToolbarChange}
                />
              ) : (
                <PatrolVideoPlayer
                  videos={videoUrls}
                  initialVideoIndex={0}
                  autoPlay={autoPlay}
                  imageUrl={imageUrl}
                  pins={pins}
                  selectedPatrol={selectedPatrol}
                  selectedPatrolSequenceId={selectedPatrolSequenceId}
                  leftPanelWidth={leftPanelWidth}
                  hideVideoTimeline={module === "compare-patrol"}
                  syncMode={syncMode}
                  controlledIsPlaying={controlledIsPlaying}
                  controlledCurrentTime={controlledCurrentTime}
                  controlledVideoIndex={controlledVideoIndex}
                  onPlayPauseChange={onPlayPauseChange}
                  onTimeUpdate={onTimeUpdate}
                  onVideoIndexChange={onVideoIndexChange}
                  controlledYaw={controlledYaw}
                  controlledPitch={controlledPitch}
                  onRotationChange={onRotationChange}
                  controlledFov={controlledFov}
                  onFovChange={onFovChange}
                  controlledViewMode={controlledViewMode}
                  onViewModeChange={onViewModeChange}
                  controlledAutoRotate={controlledAutoRotate}
                  onAutoRotateChange={onAutoRotateChange}
                />
              )
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  color: "#6B7280",
                }}
              >
                No video URLs available for this patrol
              </Box>
            )}
          </Box>
        ) : (
          <PatrolViewerEmptyState />
        )}
      </Box>
    </Paper>
  );
}
